import {Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetUserQuery} from '@dms/api/customer';
import {useGetCebiaReportsQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {getNaturalPersonFullName, OlderReportRow, OlderReportsList, Widget} from '@dms/shared';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {CebiaWidgetLogo} from './CebiaWidgetLogo';

interface CebiaReportWidgetProps extends TestIdProps {
  vehicleId?: string;
}

export function CebiaReportWidget(props: CebiaReportWidgetProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: reports = []} = useGetCebiaReportsQuery(
    {vehicleId: props.vehicleId!},
    {skip: isNil(props.vehicleId)}
  );

  const [newestReport, ...olderReports] = reports;

  const {data: user} = useGetUserQuery(
    {id: newestReport?.createdBy ?? ''},
    {skip: isNilOrEmpty(newestReport?.createdBy)}
  );

  const userFullName = getNaturalPersonFullName(user);

  if (reports.length === 0) {
    return null;
  }

  return (
    <Widget.Card data-testid={suffixTestId('cebiaReportWidget', props)}>
      <Widget.Header
        logo={<CebiaWidgetLogo alt={i18n.t('page.integrations.labels.cebiaReportTitle')} />}
        title={i18n.t('page.integrations.labels.cebiaReportTitle')}
        subtitle={
          <VStack spacing={2}>
            <Text size="xSmall">
              {i18n.t('page.integrations.labels.cebiaReportWidgetDescription')}
            </Text>
            <Text size="xSmall" color="tertiary">
              {`${i18n.t('entity.cebiaReport.recentReport')} ${
                newestReport?.createdAt
                  ? formatDateTime('dateShort', parseDate(newestReport.createdAt))
                  : ''
              }`}
              {isNotNilOrEmpty(userFullName) ? `, ${userFullName}` : ''}
            </Text>
          </VStack>
        }
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            rightIcon: 'action/launch',
            onClick: () => window.open(newestReport?.url),
            title: i18n.t('page.integrations.labels.cebiaReportViewResults'),
            'data-testid': suffixTestId('cebiaReportWidget-viewResults', props),
          },
        ]}
        data-testid={suffixTestId('cebiaReportWidget', props)}
      />
      <Show when={isPositive(olderReports.length)}>
        <OlderReportsList
          title={i18n.t('entity.cebiaReport.previousReports')}
          data-testid={props['data-testid']}
        >
          {olderReports.map((report, index) => (
            <OlderReportRow
              key={report.id}
              href={report.url}
              title={i18n.t('entity.cebiaReport.previousReport', {
                date: formatDateTime('dateShort', parseDate(report.createdAt)),
              })}
              index={index}
              createdBy={report.createdBy}
              data-testid={suffixTestId(`cebiaReportWidget-previousReport-[${index}]`, props)}
            />
          ))}
        </OlderReportsList>
      </Show>
    </Widget.Card>
  );
}
